import { motion } from 'framer-motion';
import { Button } from '../../../ui';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';

interface CompletionStepProps {
  firstName: string;
  onFinish: () => void;
  isLoading?: boolean;
}

export function CompletionStep({ firstName, onFinish, isLoading = false }: CompletionStepProps) {
  return (
    <div className="text-center">
      {/* Celebration animation */}
      <motion.div
        className="relative inline-block mb-8"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
      >
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center">
          <CheckCircleIcon className="w-14 h-14 text-white" />
        </div>
        {/* Sparkles */}
        <motion.div
          className="absolute -top-2 -right-2"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
        >
          <SparklesIcon className="w-8 h-8 text-primary-400" />
        </motion.div>
        <motion.div
          className="absolute -bottom-1 -left-3"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
        >
          <SparklesIcon className="w-6 h-6 text-accent-300" />
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-surface-800 dark:text-white mb-3">
          You're all set, {firstName}!
        </h2>
        <p className="text-surface-600 dark:text-surface-400 mb-8 max-w-md mx-auto">
          Your journey of recovery is unique and precious. Remember: one day at a time, one step at a time.
        </p>
      </motion.div>

      {/* Big Book quote */}
      <motion.blockquote
        className="bg-primary-50 dark:bg-primary-900/20 rounded-2xl p-6 mb-8 border border-primary-100 dark:border-primary-800/30"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <p className="font-serif text-lg italic text-primary-800 dark:text-primary-200 mb-3">
          "We will suddenly realize that God is doing for us what we could not do for ourselves."
        </p>
        <footer className="text-sm text-primary-600 dark:text-primary-400">— The Big Book, Page 84</footer>
      </motion.blockquote>

      {/* What's next */}
      <motion.div
        className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700 text-left mb-8"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h3 className="font-semibold text-surface-800 dark:text-white mb-3">What's next:</h3>
        <ul className="space-y-2 text-sm text-surface-600 dark:text-surface-400">
          <li className="flex items-start gap-2">
            <span className="text-accent-500 mt-0.5">✓</span>
            Complete your first daily check-in
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-500 mt-0.5">✓</span>
            Read today's daily reflection
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-500 mt-0.5">✓</span>
            Add three things you're grateful for
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-500 mt-0.5">✓</span>
            Explore the Big Book reader
          </li>
        </ul>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}>
        <Button onClick={onFinish} size="lg" className="w-full" isLoading={isLoading} disabled={isLoading}>
          {isLoading ? 'Saving your profile...' : 'Go to Dashboard'}
        </Button>
      </motion.div>
    </div>
  );
}
